import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useSearchParams } from 'react-router-dom';
import Analytics from '../utils/analytics';
import './ArtisanProfile.css';

const DAYS = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'
];

const DEFAULT_SCHEDULE = DAYS.reduce((acc, day) => {
    acc[day] = { active: false, start: '09:00', end: '17:00' };
    return acc;
}, {});

function ArtisanProfile() {
    const [searchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') === 'schedule' ? 'schedule' : 'area';

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [activeTab, setActiveTab] = useState(initialTab);

    // Sync activeTab with URL params if they change
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'schedule') {
            setActiveTab('schedule');
        } else {
            // Default to 'area' (Profile) if no tab or explicit 'area'
            setActiveTab('area');
        }
    }, [searchParams]);

    // Profile State
    const [displayName, setDisplayName] = useState('');
    const [bio, setBio] = useState('');
    const [address, setAddress] = useState('');
    const [radius, setRadius] = useState(10);
    const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);

    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const response = await api.get('/api/artisan/profile');
            if (response.ok) {
                const data = await response.json();
                setDisplayName(data.displayName || '');
                setBio(data.bio || '');
                setRadius(data.serviceRadiusKm || 10);
                if (data.homeLocation && data.homeLocation.address) {
                    setAddress(data.homeLocation.address);
                }
                if (data.weeklySchedule && Object.keys(data.weeklySchedule).length > 0) {
                    // Merge with default to ensure all days exist
                    setSchedule(prev => ({ ...prev, ...data.weeklySchedule }));
                }
            } else if (response.status === 404) {
                // 404 is expected for new artisans
                setIsEditing(true); // Auto-enable edit mode for new profiles
            } else {
                throw new Error('Failed to load profile');
            }
        } catch (err) {
            // 404 is expected for new artisans
            if (err.message && !err.message.includes('404')) {
                setError('Failed to load profile');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation: Start time < End time
        for (const day of DAYS) {
            const s = schedule[day];
            if (s.active) {
                if (s.start >= s.end) {
                    setError(`Invalid schedule for ${day}: End time must be after start time.`);
                    return;
                }
            }
        }

        // Basic lat/lng (Mock for now, would use Geocoding API)
        let lat = 49.2827;
        let lng = -123.1207;

        const payload = {
            displayName,
            bio,
            serviceRadiusKm: parseFloat(radius),
            homeLocation: {
                address,
                lat,
                lng
            },
            weeklySchedule: schedule,
            // skills: selectedSkills // Removed to rely on backend logic handling nulls
            servicesOffered: []
        };

        try {
            const res = await api.put('/api/artisan/profile', payload);
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.details || data.message || 'Failed to save profile');
            }
            setSuccess('Profile saved successfully!');
            setIsEditing(false); // Exit edit mode
            window.scrollTo(0, 0);

            // Analytics: Track artisan onboarding completion
            Analytics.pushCustomEvent('artisan_onboarding_complete');
        } catch (err) {
            setError(err.message || 'Failed to save profile');
            window.scrollTo(0, 0);
        }
    };

    const updateDay = (day, field, value) => {
        setSchedule(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value
            }
        }));
    };

    if (loading) return (
        <div className="artisan-profile-page">
            <div className="artisan-profile-container" style={{ paddingTop: '4rem', textAlign: 'center' }}>
                <div className="form-hint">Loading profile...</div>
            </div>
        </div>
    );

    return (
        <div className="artisan-profile-page">
            <div className="artisan-profile-container section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} className="profile-header fade-in-section in-view">
                    <div>
                        <h1 className="profile-title">{activeTab === 'schedule' ? 'Availability' : 'Artisan Profile'}</h1>
                        <p className="profile-subtitle">
                            {activeTab === 'schedule'
                                ? 'Set your weekly schedule and working hours.'
                                : 'Manage your public profile information.'}
                        </p>
                    </div>
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="btn btn-primary"
                        >
                            Edit Profile
                        </button>
                    )}
                </div>

                {/* Tabs removed to separate views */}

                {error && (
                    <div className="message-box error fade-in-section in-view">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="message-box success fade-in-section in-view">
                        {success}
                    </div>
                )}

                <form onSubmit={handleSave} className="profile-form fade-in-section in-view">
                    {activeTab === 'area' ? (
                        <div className="space-y-6">
                            <div className="form-group">
                                <label>Display Name</label>
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    className="form-input"
                                    placeholder="e.g. Jane Doe"
                                    required
                                    disabled={!isEditing}
                                />
                            </div>

                            <div className="form-group">
                                <label>Bio</label>
                                <textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    className="form-textarea"
                                    placeholder="Tell clients about yourself, your expertise, and your passion for beauty..."
                                    disabled={!isEditing}
                                />
                            </div>

                            <div className="form-group">
                                <label>Home Base Address</label>
                                <input
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    className="form-input"
                                    placeholder="Street, City, Postal Code"
                                    disabled={!isEditing}
                                />
                                <p className="form-hint">
                                    This location is used privately to calculate travel time to clients. It is never shared.
                                </p>
                            </div>

                            <div className="form-group">
                                <label>
                                    Travel Radius: {radius} km
                                </label>
                                <div className="range-slider-container">
                                    <input
                                        type="range"
                                        min="1"
                                        max="50"
                                        value={radius}
                                        onChange={(e) => setRadius(e.target.value)}
                                        className="range-slider"
                                        disabled={!isEditing}
                                    />
                                    <div className="range-labels">
                                        <span>1 km</span>
                                        <span>50 km</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="schedule-list">
                            <p className="form-hint" style={{ marginBottom: '1.5rem' }}>Set your standard weekly working hours. You can adjust specific dates in your calendar.</p>

                            {DAYS.map(day => (
                                <div key={day} className="schedule-item">
                                    <div className="day-label">
                                        <input
                                            type="checkbox"
                                            checked={schedule[day].active}
                                            onChange={(e) => updateDay(day, 'active', e.target.checked)}
                                            className="day-checkbox"
                                            disabled={!isEditing}
                                        />
                                        <span className="day-name">{day}</span>
                                    </div>

                                    {schedule[day].active ? (
                                        <div className="time-inputs">
                                            <input
                                                type="time"
                                                value={schedule[day].start}
                                                onChange={(e) => updateDay(day, 'start', e.target.value)}
                                                className="time-input"
                                                disabled={!isEditing}
                                            />
                                            <span className="time-separator">-</span>
                                            <input
                                                type="time"
                                                value={schedule[day].end}
                                                onChange={(e) => updateDay(day, 'end', e.target.value)}
                                                className="time-input"
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    ) : (
                                        <span className="day-off">Day Off</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {isEditing && (
                        <div className="form-actions" style={{ gap: '1rem' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(false);
                                    fetchProfile(); // Revert changes
                                }}
                                className="btn"
                                style={{ background: 'transparent', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                Save Profile
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default ArtisanProfile;
